import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Project } from 'src/app/models/project.model';
import { ProjectCartService } from './project-cart.service';

/**
 * Every configurator screen now belongs to a project — a single-configuration
 * run included. This keeps a deep link or a refresh from dropping the user
 * into the configurator with no cart to save into.
 *
 * It is also the one place that knows the project's mode early enough to keep
 * a single run off /cart, which is a project-only surface: a single
 * configuration reviews and submits straight from /order-details.
 *
 * canActivate only, matching AuthGuard; lazy chunks are not gated separately.
 */
@Injectable({ providedIn: 'root' })
export class ProjectGuard implements CanActivate {
  constructor(private cart: ProjectCartService, private router: Router) { }

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // A refresh loses the in-memory project but not the pointer to it.
    const project$: Observable<Project | null> = this.cart.hasActiveProject
      ? of(this.cart.project)
      : this.cart.restoreActiveProject();

    return project$.pipe(
      map(project => {
        if (!project) {
          return this.router.createUrlTree(['/mode']);
        }

        // /cart is the project cart. A single run has no cart page; send it to
        // the screen it actually finishes on.
        if (state.url.split('?')[0] === '/cart' && project.mode === 'single') {
          return this.router.createUrlTree(['/order-details']);
        }

        return true;
      }),
      catchError(() => of(this.router.createUrlTree(['/mode'])))
    );
  }
}
